import { useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

import { SplashScreen } from '../../components/SplashScreen';
import {
  PATH_MISSION_DEFAULT,
  PATH_MISSION_REPORTS,
} from '../../app/constants/paths.constant';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { useMissionBreadcrumbPaths } from '../../hooks/useMissionBreadcrumbPaths.hook';
import { useMissionSpaceWording } from '../../hooks/useMissionSpaceWording.hook';
import { Loading } from '../../components/Loading';
import { AttachedFile } from '../../components/Illustrations/AttachedFile';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { useMissionContext } from '../../contexts/MissionContext/hooks/useMissionContext.hook';
import { useReportContext } from '../../contexts/ReportContext/hooks/useReportContext.hook';
import { ReportContextStatus } from '../../contexts/ReportContext/types/report-context.type';
import { buildDocumentTitle } from '../../utils/document-title.util';
import { PageTitle } from '../../components/PageTitle';

import { MissionReportReady } from './Ready';

interface Props {
  readonly isInPrintMode?: boolean;
}

export const MissionReportPage = ({
  isInPrintMode = false,
}: Readonly<Props>) => {
  const navigate = useNavigate();

  const { folderId, missionId, mission } = useMissionContext();

  const missionBreadcrumbPaths = useMissionBreadcrumbPaths();
  const missionWording = useMissionSpaceWording();
  const reportContext = useReportContext();

  const { paths: missionPaths } = useBuildPaths(
    {
      default: PATH_MISSION_DEFAULT,
      reports: PATH_MISSION_REPORTS,
    },
    { folderId, missionId },
  );

  const goToReportsList = useCallback(
    () => void navigate(missionPaths.reports),
    [navigate, missionPaths.reports],
  );

  const beforeTitleContent = (
    <>
      <Helmet>
        <title>
          {buildDocumentTitle(
            `Fiche Rapport ${missionWording.ofName}`,
            mission?.code,
          )}
        </title>
      </Helmet>
      <Breadcrumbs
        paths={[
          ...missionBreadcrumbPaths,
          {
            to: missionPaths.reports,
            label: 'rapports',
            key: 'reports',
          },
        ]}
      />
    </>
  );

  const titleContent = (
    <PageTitle title={`Fiche Rapport ${missionWording.ofName}`} />
  );

  if (reportContext.status === ReportContextStatus.Ready) {
    return <MissionReportReady isInPrintMode={isInPrintMode} />;
  }

  if (reportContext.status === ReportContextStatus.NotInitialized) {
    return (
      <>
        {beforeTitleContent}
        {titleContent}
        <SplashScreen
          action={{ label: 'Retour aux rapports', onClick: goToReportsList }}
          illustration={<AttachedFile />}
          message="Ce rapport n'a pas (ou plus) de document associé - il est peut-être encore en cours de création."
        />
      </>
    );
  }

  return (
    <>
      <Loading />
      {beforeTitleContent}
      {titleContent}
    </>
  );
};
